/**
 * Placeholder province tint palette — v0M2.4a.
 *
 * Provides a per-province colour for the political-tint overlay layer
 * (MAP.md §8 layer 2). These are stand-ins until scenario generation
 * assigns real factions to provinces; when factions land, the tint
 * source shifts to `Province.ownerFactionId` and the palette moves to
 * the Okabe-Ito faction palette per STYLE_GUIDE.md.
 *
 * Colours picked to sit above the earth-toned terrain palette without
 * fighting it — saturated but light, applied at low alpha in the
 * renderer.
 */

import type { Rgb } from './terrain-palette';

/** 12 province-tint colours, cycled by (provinceId - 1) % PALETTE.length. */
export const PROVINCE_TINT_PALETTE: readonly Rgb[] = [
  [214, 176,  85], // gold — Karthholm (central capital)
  [ 94, 138, 106], // sage — Highford (western forest march)
  [ 92, 138, 176], // steel-blue — Portroth (coastal)
  [188, 100,  76], // rust — Redstone (frontier desert)
  [200, 210, 220], // ice — Icemarch (northern mountains)
  [176, 148, 200], // amethyst — Silverford (NE ridge)
  [130, 172, 100], // olive — Greenvale (western forest interior)
  [138, 130,  92], // fen-grey — Fenlands (lake / swamp)
  [220, 172,  80], // amber — Sunport (east coast)
  [154, 130,  92], // ochre — Ironhill (central hills)
  [196, 168, 110], // wheat — Oxford (east farmland)
  [ 90,  92, 110], // slate — Blackmarsh (deep south swamp)
];

/**
 * Colour for a given province id (1-indexed). Cycles the palette for
 * ids beyond the 12-entry base.
 */
export function provinceTint(provinceId: number): Rgb {
  const idx = (provinceId - 1) % PROVINCE_TINT_PALETTE.length;
  return PROVINCE_TINT_PALETTE[idx] ?? [128, 128, 128];
}
